import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  LinearProgress,
  Alert,
  CssBaseline,
  IconButton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SortIcon from "@mui/icons-material/Sort";
import YouTubeIcon from "@mui/icons-material/YouTube";
import axios from "axios";
import { Link } from "react-router-dom";
import CreditsBanner from "../Components/CreditsNudge";
import {
  ContentBox,
  StyledButton,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import YouTubeKeywordSuggestContent from "../Contents/YtKeywordSuggestionContent.js";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

function YouTubeKeywordSuggestion({ user, login, isLoggedIn }) {
  const [keyword, setKeyword] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    const storedKeyword = localStorage.getItem("primaryInputText");
    if (storedKeyword) {
      setKeyword(storedKeyword);
      localStorage.removeItem("primaryInputText");
    }
  }, []);

  const handleKeywordChange = (e) => {
    const value = e.target.value;
    if (value.length <= 120) {
      setKeyword(value);
    }
  };

  const getDifficulty = (keyword) => {
    const lowerKeyword = keyword.toLowerCase();
    const wordCount = lowerKeyword.split(" ").length;

    if (
      wordCount >= 6 ||
      /apna college|bro code|durgasoft|free code camp|full course/.test(
        lowerKeyword
      )
    ) {
      return "Easy";
    } else if (
      /crash course|tutorial|android|english|download|book|geeksforgeeks/.test(
        lowerKeyword
      )
    ) {
      return "Medium";
    } else {
      return "Hard";
    }
  };

  const handleGenerateSuggestions = async () => {
    setSuccess("");
    setError("");

    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500);
      return;
    }

    const trimmedKeyword = keyword.trim();
    if (!trimmedKeyword) {
      setError("Please enter a keyword.");
      return;
    }

    if (trimmedKeyword.length > 120) {
      setError("Keyword must be 120 characters or fewer.");
      return;
    }

    if (credits.remaining <= 0) {
      setError("Not enough credits to see suggested keywords.");
      return;
    }

    setLoading(true);
    setSuggestions([]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/youtube/keyword-auto-suggestion`,
        { youtube_search_query: keyword },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      const rawKeywords = response.data.keywords || [];

      const keywordSuggestions = rawKeywords.map((kw) => ({
        keyword: kw,
        difficulty: getDifficulty(kw),
      }));

      setSuggestions(keywordSuggestions);
      setSuccess(response.data.successMessage || "Suggestions generated!");

      const creditsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/credit`
      );

      setCredits({
        remaining: creditsResponse.data.remaining_credits || 0,
        availed: creditsResponse.data.availed_credits || 0,
        total: creditsResponse.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      console.error("Error fetching keyword suggestions:", err);
      setError(
        err.response?.data?.errorMessage ||
          "Failed to fetch keyword suggestions. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const copyTableToClipboard = () => {
    const header = ["Keyword", "Seed Keyword", "Source", "Difficulty"];
    const rows = suggestions.map((suggestion) => [
      suggestion.keyword,
      keyword,
      "YouTube",
      suggestion.difficulty,
    ]);

    const csvContent = [header, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    navigator.clipboard.writeText(csvContent);
    setSuccess("Table copied to clipboard!");
  };

  const downloadCSV = () => {
    const header = ["Keyword", "Seed Keyword", "Source", "Difficulty"];
    const rows = suggestions.map((suggestion) => [
      suggestion.keyword,
      keyword,
      "YouTube",
      suggestion.difficulty,
    ]);

    const csvContent = [header, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "youtube_keyword_suggestions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSort = () => {
    const sorted = [...suggestions].sort((a, b) => {
      const order = ["Easy", "Medium", "Hard"];
      return sortAsc
        ? order.indexOf(a.difficulty) - order.indexOf(b.difficulty)
        : order.indexOf(b.difficulty) - order.indexOf(a.difficulty);
    });
    setSuggestions(sorted);
    setSortAsc(!sortAsc);
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            YouTube Keyword Suggestions 💡
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Enter a keyword and get auto suggestions from YouTube.
          </SubtitleText>

          <Box mb={3}>
            <TextField
              fullWidth
              label="Enter Seed Keyword"
              variant="outlined"
              value={keyword}
              onChange={handleKeywordChange}
              placeholder="e.g. youtube seo, javascript tutorial"
              size="medium"
              inputProps={{ maxLength: 60 }}
              helperText={`${keyword.length}/60 characters`}
              id="youtube-keyword-suggestion"
              name="primary-textbox"
            />
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <StyledButton
              variant="contained"
              color="success"
              onClick={handleGenerateSuggestions}
            >
              Generate Suggestions
            </StyledButton>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              <b> 💡 1 Search keyword = 1 Credit</b>
              <br />
              Enter the keyword to get YouTube auto-suggested keywords. Have
              questions? <Link to="/contact">Contact support</Link>
            </Typography>
          </Box>

          {suggestions.length > 0 && (
            <Box mt={3}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body1" fontWeight="bold">
                  Suggested Keywords:
                </Typography>
                <Box>
                  <IconButton
                    onClick={copyTableToClipboard}
                    size="small"
                    title="Copy table"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    onClick={downloadCSV}
                    size="small"
                    title="Download CSV"
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  overflowX: "auto",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ backgroundColor: "#f0f0f0" }}>
                    <tr>
                      <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                        Keyword
                      </th>
                      <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                        Seed Keyword
                      </th>
                      <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                        <Box display="flex" justifyContent="center">
                          Source
                        </Box>
                      </th>
                      <th style={{ padding: "8px", border: "1px solid #ccc" }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          gap={1}
                        >
                          <span>Difficulty</span>
                          <IconButton
                            onClick={handleSort}
                            size="small"
                            title="Sort by Difficulty"
                            sx={{ padding: 0 }}
                          >
                            <SortIcon
                              fontSize="small"
                              style={{
                                transform: sortAsc
                                  ? "rotate(0deg)"
                                  : "rotate(180deg)",
                              }}
                            />
                          </IconButton>
                        </Box>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {suggestions.map((suggestion, index) => (
                      <tr key={index}>
                        <td
                          style={{ padding: "8px", border: "1px solid #ccc" }}
                        >
                          {suggestion.keyword}
                        </td>
                        <td
                          style={{ padding: "8px", border: "1px solid #ccc" }}
                        >
                          {keyword}
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            textAlign: "center",
                          }}
                        >
                          <YouTubeIcon fontSize="small" />
                        </td>
                        <td
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            textAlign: "center",
                          }}
                        >
                          {suggestion.difficulty}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>
          )}
        </ContentBox>
        <CommentSection
          toolId="youtube/keyword-auto-suggestion"
          user={user}
          login={login}
          isLoggedIn={isLoggedIn}
        />
        <YouTubeKeywordSuggestContent />
      </PageContainer>
    </>
  );
}

export default YouTubeKeywordSuggestion;
